import React from 'react';
import { Callout, ColorPicker, DefaultButton, DirectionalHint, Dropdown, Icon, IDropdownOption, Pivot, PivotItem, useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import FontPicker from '../../Inputs/FontPicker';
import {
    activePaletteColors,
    getActivePalette,
    isStyleToken,
    PALETTE_SLOT_LABELS,
    PALETTE_SLOTS,
    PALETTE_TOKEN_PREFIX,
    PaletteSlot,
    paletteSlotOfToken,
    resolveStyleToken,
} from './stylesConfig';
import { useStorefrontStyles } from './StorefrontStylesContext';
import { DynamicSetting } from '../../Common/Setting';

export interface IStyleEntry {
    label: string;
    type: 'color' | 'font' | 'select';
    value?: string;
    inheritedValue?: string;
    // For type 'color': show the alpha slider (value comes back as rgba()). Off by default —
    // most storefront colors are solid fills.
    alpha?: boolean;
    // For type 'select': the choices shown in the dropdown.
    options?: IDropdownOption[];
    /**
     * Offer the active Color Palette / Font Pack as pickable values, stored as a `$palette:<slot>`
     * / `$font:<slot>` TOKEN rather than a literal.
     *
     * OPT-IN on purpose. This control also drives raw-CSS surfaces (the section editor's StyleProps
     * rows, the content-section forms) whose values are written straight into a `style` prop or a
     * CSS declaration without ever passing through the token resolvers - a token written there
     * would reach the browser verbatim. Only switch it on where the value flows through
     * resolveElementStyle / resolveStyleProps / resolvedStyleValue.
     *
     * With no active palette (or no styles context at all) this is inert: the picker behaves
     * exactly as it does today.
     */
    allowPaletteTokens?: boolean;
    /**
     * Deep-link identifier for this entry, e.g. `storefront.header.menuBarColor`.
     *
     * The label cannot serve as one: it is display copy, and several editors reuse the same words
     * ("Bar Color", "Background") for different settings. Set it wherever the entry is a documented
     * setting; entries without one are simply not linkable.
     */
    settingKey?: string;
    onChange: (value: string) => void;
    onClear: () => void;
}

interface IStorefrontColorPickerButtonProps {
    label: string;
    entries: IStyleEntry[];
    style?: React.CSSProperties;
}

// The palette swatch strip at the head of a colour picker: one chip per slot of the ACTIVE palette.
// Clicking a chip stores the token, so the value keeps tracking the palette instead of freezing a
// copy of today's colour.
const PaletteSwatchStrip: React.FC<{
    paletteName: string;
    colors: Record<string, unknown>;
    selectedSlot?: PaletteSlot;
    onPick: (slot: PaletteSlot) => void;
}> = ({ paletteName, colors, selectedSlot, onPick }) => {
    const theme = useTheme();
    const slots = PALETTE_SLOTS.filter((slot) => typeof colors[slot] === 'string' && !!colors[slot]);
    if (slots.length === 0) return null;
    return (
        <div style={{ marginBottom: '0.5em' }}>
            <div style={{ fontSize: 11, color: theme.palette.neutralSecondary, marginBottom: 4 }}>
                Palette: {paletteName}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {slots.map((slot) => {
                    const color = colors[slot] as string;
                    const selected = selectedSlot === slot;
                    return (
                        <button
                            key={slot}
                            type='button'
                            title={`${PALETTE_SLOT_LABELS[slot]} - ${color}`}
                            aria-label={PALETTE_SLOT_LABELS[slot]}
                            aria-pressed={selected}
                            onClick={() => onPick(slot)}
                            style={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                cursor: 'pointer',
                                borderRadius: 3,
                                background: color,
                                // The selected slot gets the brand ring rather than a check mark -
                                // a glyph would be unreadable on half these colours.
                                border: selected
                                    ? `2px solid ${theme.palette.themePrimary}`
                                    : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                boxShadow: selected ? `0 0 0 1px ${theme.palette.white} inset` : undefined,
                            }}
                        />
                    );
                })}
            </div>
            <div style={{ fontSize: 11, color: theme.palette.neutralTertiary, marginTop: 4 }}>
                Pick a palette color to keep this in sync with the palette.
            </div>
        </div>
    );
};

/**
 * Wraps a picker in its deep-link marker when the entry carries a settingKey.
 *
 * Only the open entry is in the DOM (the others are unmounted Pivot items), so ids stay unique even
 * though several editors render this control.
 */
const MarkedStyleEntry: React.FC<{ entry: IStyleEntry }> = ({ entry }) =>
    entry.settingKey
        ? <DynamicSetting id={entry.settingKey}><StyleEntryPicker entry={entry} /></DynamicSetting>
        : <StyleEntryPicker entry={entry} />;

const StyleEntryPicker: React.FC<{ entry: IStyleEntry }> = ({ entry }) => {
    const ctx = useStorefrontStyles();
    // Without a provider this is an empty list, so every lookup below is undefined and the control
    // renders exactly as it always has.
    const palette = entry.allowPaletteTokens ? getActivePalette(ctx.styles) : undefined;
    const colors = palette ? activePaletteColors(ctx.styles) : undefined;
    const isToken = isStyleToken(entry.value);
    const resolved = isToken ? resolveStyleToken(entry.value as string, ctx.styles) : undefined;

    if (entry.type === 'font') {
        return (
            <div style={{ minWidth: 240 }}>
                <FontPicker
                    selectedFont={entry.value || ''}
                    onChange={(font) => font ? entry.onChange(font) : entry.onClear()}
                    placeholder={entry.inheritedValue ? `Inherited: ${entry.inheritedValue}` : 'Default'}
                    allowPaletteTokens={entry.allowPaletteTokens}
                />
                {entry.inheritedValue && !entry.value && (
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                        Inheriting: {entry.inheritedValue}
                    </div>
                )}
            </div>
        );
    }

    if (entry.type === 'select') {
        return (
            <div style={{ minWidth: 240 }}>
                <Dropdown
                    selectedKey={entry.value ?? ''}
                    options={entry.options ?? []}
                    onChange={(_, o) => entry.onChange((o?.key as string) || '')}
                />
            </div>
        );
    }

    return (
        <div>
            {palette && colors && (
                <PaletteSwatchStrip
                    paletteName={palette.name}
                    colors={colors}
                    selectedSlot={paletteSlotOfToken(entry.value)}
                    onPick={(slot) => entry.onChange(`${PALETTE_TOKEN_PREFIX}${slot}`)}
                />
            )}
            <ColorPicker
                // A tokenized value shows the colour it currently resolves to; dragging the picker
                // writes a literal, which is the same "detach" the button below does explicitly.
                color={resolved || entry.value || entry.inheritedValue || '#ffffff'}
                onChange={(_, color) => entry.onChange(color.str)}
                alphaType={entry.alpha ? 'alpha' : 'none'}
            />
            {(entry.value || isToken) && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {isToken && resolved && (
                        <DefaultButton
                            text='Detach'
                            title={`Replace the palette link with ${resolved}, so it stops following the palette`}
                            iconProps={{ iconName: 'RemoveLink' }}
                            onClick={() => entry.onChange(resolved)}
                        />
                    )}
                    <DefaultButton
                        text='Unset (inherit)'
                        iconProps={{ iconName: 'Undo' }}
                        onClick={entry.onClear}
                    />
                </div>
            )}
        </div>
    );
};

const StorefrontColorPickerButton: React.FC<IStorefrontColorPickerButtonProps> = (props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const buttonId = useId('color-btn');
    const ctx = useStorefrontStyles();
    const colorEntries = props.entries.filter(e => e.type === 'color');

    return (
        <>
            <button
                id={buttonId}
                onClick={() => setIsOpen(!isOpen)}
                title={`Edit ${props.label}`}
                className='storefront-color-picker-button'
                style={props.style}
            >
                {colorEntries.map((c, i) => {
                    // A tokenized value paints the colour it RESOLVES to (the raw `$palette:accent`
                    // string is not a colour), with a small link glyph so the chip still reads as
                    // "following the palette" rather than "someone typed this hex".
                    const token = isStyleToken(c.value) ? c.value : undefined;
                    const resolved = token ? resolveStyleToken(token, ctx.styles) : undefined;
                    const shown = resolved || (token ? undefined : c.value) || c.inheritedValue || '#ffffff';
                    return (
                        <span key={i} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                            <span
                                className='storefront-color-picker-swatch'
                                style={{ backgroundColor: shown }}
                                title={token ? `${c.label}: ${PALETTE_SLOT_LABELS[paletteSlotOfToken(token) as PaletteSlot] ?? token} (palette)` : c.label}
                            />
                            {token && (
                                <Icon
                                    iconName='Link'
                                    style={{
                                        position: 'absolute', right: -3, bottom: -3, fontSize: 8,
                                        color: '#fff', textShadow: '0 0 2px rgba(0,0,0,0.9)', pointerEvents: 'none',
                                    }}
                                />
                            )}
                        </span>
                    );
                })}
                <span className='storefront-color-picker-label'>{props.label}</span>
            </button>
            {isOpen && (
                <Callout
                    target={`#${buttonId}`}
                    onDismiss={() => setIsOpen(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    setInitialFocus
                    style={{ padding: '12px' }}
                >
                    {props.entries.length === 1 ? (
                        <MarkedStyleEntry entry={props.entries[0]} />
                    ) : (
                        <Pivot>
                            {props.entries.map((entry, i) => (
                                <PivotItem key={i} headerText={entry.label}>
                                    <div style={{ paddingTop: 8 }}>
                                        <MarkedStyleEntry entry={entry} />
                                    </div>
                                </PivotItem>
                            ))}
                        </Pivot>
                    )}
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                        <DefaultButton text='Close' onClick={() => setIsOpen(false)} />
                    </div>
                </Callout>
            )}
        </>
    );
};

export default StorefrontColorPickerButton;
